import type { Ref } from 'vue';

import {
    BackendErrorCode,
    DirectSaleComputedState,
    type DirectSaleItemEntity,
    type DirectSaleListingItemEntity,
    DirectSaleState,
    NegotiationState,
} from '~/apiClient';
import type { SaleStatus } from '~/types/saleStatus';

interface UseDirectSaleStatusReturn extends SaleStatus {
    hasOrder: ComputedRef<boolean>;
    isSold: ComputedRef<boolean>;
}

export default function useDirectSaleStatus(directSale: Ref<DirectSaleItemEntity | DirectSaleListingItemEntity>): UseDirectSaleStatusReturn {
    const { data: authData } = useAuthUtils();
    const { tt } = useTypedI18n();
    const dayjs = useDayjs();

    const userMeta = computed(() => directSale.value.userMeta);
    const purchaseInfo = computed(() => directSale.value.purchaseInfo);

    const isSeller = computed(() => authData.value?.user?.companyId === directSale.value?.sellerCompanyId);
    const hasOrder = computed(
        () => !!userMeta.value?.pendingPayment || !!userMeta.value?.paid || !!purchaseInfo.value?.isPaid || !!purchaseInfo.value?.isPendingPayment
    );
    const isSold = computed(
        () =>
            !!directSale.value.computedState &&
            [DirectSaleComputedState.Sold, DirectSaleComputedState.PendingPayment].includes(directSale.value.computedState)
    );
    const hasNoActiveOrder = computed(() => directSale.value.computedState === DirectSaleComputedState.NoActiveOrder);
    const isNotPaid = computed(() => userMeta.value?.cantBuyReason === BackendErrorCode.CantBuyDirectSaleOrderNotPaid);
    const hasActiveNegotiation = computed(
        () => directSale.value.purchaseInfo?.hasOpenNegotiation || directSale.value.negotiations?.some(n => n.state === NegotiationState.Open)
    );

    const statusSectionColor = computed(() => {
        if (isSold.value && (hasOrder.value || isSeller.value)) return 'bg-success-50';

        if (hasNoActiveOrder.value) return 'bg-primary-50';

        return 'bg-neutral-100';
    });

    const title = computed(() => {
        if (directSale.value.state === DirectSaleState.ToBeApproved) return tt('sdp.DirectSale.directSaleStatus.sellerReservePrice');

        if (isSold.value) return hasOrder.value ? tt('sdp.DirectSale.directSaleStatus.purchased') : tt('sdp.DirectSale.directSaleStatus.sold');

        if (hasActiveNegotiation.value) return tt('sdp.DirectSale.directSaleStatus.inNegotiation');

        if (hasNoActiveOrder.value) return tt('sdp.DirectSale.directSaleStatus.available');

        return tt('sdp.DirectSale.directSaleStatus.notAvailable');
    });

    const titleColor = computed(() => {
        if (isSold.value && (hasOrder.value || isSeller.value)) return 'text-success-600';

        return;
    });

    const titleAnnotation = computed(() => {
        if (isNotPaid.value) return tt('sdp.DirectSale.directSaleStatus.annotation.notPaid');

        if (hasOrder.value && directSale.value.computedState === DirectSaleComputedState.PendingPayment)
            return tt('sdp.DirectSale.directSaleStatus.annotation.pendingPayment');

        if (directSale.value && [DirectSaleState.ToBeScheduled, DirectSaleState.Waiting, DirectSaleState.Pending].includes(directSale.value.state))
            return tt('sdp.DirectSale.directSaleStatus.annotation.comingSoon');

        if (isSeller.value && directSale.value.computedState === DirectSaleComputedState.CloseUnsold)
            return tt('sdp.DirectSale.directSaleStatus.annotation.unsold');

        return;
    });

    const titleAnnotationColor = computed(() => {
        if (isNotPaid.value) return 'text-error-600';

        return 'text-default';
    });

    const priceColor = computed(() => {
        if (isSold.value && (hasOrder.value || isSeller.value)) return 'text-success-800';

        if (hasNoActiveOrder.value) return 'text-primary';

        return 'text-default';
    });

    const isFeeNoticeHidden = computed(
        () =>
            !!directSale.value &&
            ![DirectSaleState.Pending, DirectSaleState.Waiting, DirectSaleState.Open, DirectSaleState.Closed].includes(directSale.value.state)
    );

    const isBailNeeded = computed(() => userMeta.value?.cantBuyReason === BackendErrorCode.CantPerformUserNoBail);

    const hasRestrictedNotice = computed(
        () =>
            !!userMeta.value &&
            !userMeta.value?.canBuy &&
            !!userMeta.value?.cantBuyReason &&
            ![BackendErrorCode.CantBuyDirectSaleNotOpen, BackendErrorCode.CantBuyDirectSaleHasDirectSaleOrder].includes(
                userMeta.value?.cantBuyReason
            ) &&
            !isBailNeeded.value
    );

    const hasInspectionNotice = computed(() => !!userMeta.value?.inspectionExpiredExportRestrictions);

    const inspectionReminder = computed(() => {
        if (!isSeller.value) return;

        const now = dayjs();
        const target = dayjs(directSale.value.mainAsset.inspectionExpiryDate);

        if (target.isBefore(now)) return 'expired';
        if (target.isBefore(now.add(30, 'day'))) return 'expiringSoon';
    });

    return {
        statusSectionColor,
        title,
        titleColor,
        titleAnnotation,
        titleAnnotationColor,
        priceColor,
        isFeeNoticeHidden,
        hasRestrictedNotice,
        isBailNeeded,
        hasInspectionNotice,
        hasOrder,
        isSold,
        isSeller,
        inspectionReminder,
    };
}
